import { skipToken } from '@reduxjs/toolkit/query/react';
import { getScopeRole } from '@/services/current-context';

import { canManageIntegrationStatus } from '@/util/roles';
import {
  type IntegrationStatus,
  useGetIntegrationStatusQuery,
} from '../services/pmsConnectionApi';
import type { PmsAccountRef } from '../services/pmsAccount';

export interface IntegrationStatusView {
  status: IntegrationStatus;
  isLoading: boolean;
  isError: boolean;
  /**
   * An account is named and its answer hasn't arrived yet (first read, or
   * after an account change). `status` reads `none` meanwhile, which a surface
   * that makes a claim from it (the photos page's "Synced" chip) must not
   * mistake for "no PMS". False for a null account and after a failed read.
   */
  isPending: boolean;
  /** Role may pause/resume AND there is something to pause/resume. */
  canManage: boolean;
}

const NONE: IntegrationStatus = { kind: 'none' };

/**
 * The one way UI reads the account's PMS integration status (DV-2549). RTK
 * dedupes concurrent subscribers to a single request and a status mutation
 * anywhere refreshes every subscriber via tag invalidation.
 *
 * Fails OPEN: loading and error both read as `none`, so every surface falls
 * back to its pre-DV-2549 behavior — never to "disabled".
 *
 * DV-3345: the caller names the account (the session's on /home, the
 * property's on the Global Orders photos page); a null account skips the query
 * and reads `none` like any other fail-open case. `currentData`, never `data`:
 * when the account changes, RTK keeps the PREVIOUS account's `data` until the
 * new request settles, which would show one client's paused state on another.
 *
 * `refetchOnFocus` is opt-in: the Global Orders photos page sends an Internal
 * Admin to the client's Integrations page in a NEW tab (spec D9), and a resume
 * there never invalidates this tab's cache, so that page re-reads on return.
 * /home callers keep the default.
 */
export function useIntegrationStatus(
  account: PmsAccountRef | null,
  options: { refetchOnFocus?: boolean } = {},
): IntegrationStatusView {
  const { currentData, error, isLoading, isError } = useGetIntegrationStatusQuery(account ?? skipToken, {
    refetchOnFocus: options.refetchOnFocus ?? false,
  });
  const status = currentData ?? NONE;
  // `error`, not only `isError`: a refetch (focus) puts a failed entry back to
  // `pending`, which clears isError but keeps the entry's error until an answer
  // lands. Without it a never-succeeded read flips back to pending on every
  // retry and the chip it gates flickers (DV-3345 review).
  const isPending = account !== null && currentData === undefined && !isError && error === undefined;
  const roleAllows = canManageIntegrationStatus(getScopeRole());
  const canManage = roleAllows && (status.kind === 'active' || status.kind === 'disabled');
  return { status, isLoading, isError, isPending, canManage };
}
